import type { IconName } from '../../components/ui/Icon.js';

/**
 * Account configuration menu.
 *
 * Structured after the feature taxonomy a hosted-PBX customer expects — call
 * management, contact centre, media, numbers, reporting, billing — rather than
 * after our table layout. Customers look for "after-hours routing", not for the
 * table it happens to live in.
 *
 * `phase` marks a section whose backend does not exist yet. Placeholders are otherwise NOT
 * listed (owner, 26 September 2026: hide them); Porting stays, because clients will submit porting
 * requests from it. `permission` hides what this user may not touch.
 */
export interface ConfigItem {
  key: string;
  label: string;
  /** Set when there is no backend yet; renders a labelled placeholder. */
  phase?: string;
  permission?: string;
  /** The account module it belongs to (0130): hidden while the module is off. */
  module?: 'dialler' | 'auto_qa';
  /** A part of the platform that may be switched off (API /platform-features): hidden while it is. */
  feature?: 'conversations' | 'teams';
  /**
   * Words people search for that are not the label ("invoice" finds Balance & payments), for the
   * menu's Find a page box (UX review, 21 October 2026).
   */
  keywords?: string[];
}

export interface ConfigGroup {
  key: string;
  label: string;
  icon: IconName;
  items: ConfigItem[];
}

export const CONFIG_NAV: ConfigGroup[] = [
  // Grouped by what a person is doing (owner, 26 September 2026). Ten groups, not thirteen (UX
  // review, 21 October 2026, owner: "mockups look good"): Connectivity joins Numbers, Media joins
  // Voice features, and the small groups are gone. Keys (and so addresses) are unchanged; group keys
  // are kept where a group survived, so remembered open groups still open.
  {
    key: 'overview', label: 'Overview', icon: 'dashboard',
    items: [
      { key: 'dashboard', label: 'Dashboard', keywords: ['home', 'overview', 'summary'] },
    ],
  },
  {
    // What is happening now, for the account's own people and for staff who open it (30 September 2026).
    key: 'realtime', label: 'Live', icon: 'activity',
    items: [
      { key: 'live-extensions', label: 'Live extensions', permission: 'extension:read', keywords: ['who is on a call', 'presence', 'online'] },
      { key: 'live-queues', label: 'Live queues', permission: 'queue:read', keywords: ['waiting', 'agents now'] },
      { key: 'wallboard', label: 'Wallboards', permission: 'queue:read', keywords: ['tv', 'screen'] },
    ],
  },
  {
    key: 'people', label: 'People & phones', icon: 'users',
    items: [
      { key: 'extensions', label: 'Extensions', permission: 'extension:read', keywords: ['seat', 'user', 'phone', 'sip password', 'caller id', 'forwarding'] },
      // Handsets are set up on each extension, under Phone setup.
      { key: 'departments', label: 'Departments', permission: 'extension:read', keywords: ['cost centre', 'team'] },
      { key: 'portal-users', label: 'Portal users', permission: 'user:read', keywords: ['invite', 'login', 'access', 'role'] },
      // Everything about how a handset GETS its configuration lives here (the provisioning module's portal half).
      { key: 'provisioning', label: 'Provisioning', permission: 'device:read', keywords: ['yealink', 'fanvil', 'mac address', 'handset', 'desk phone'] },
      // A handset model, its keys and its network, shared by many phones (docs/29).
      { key: 'templates', label: 'Phone templates', permission: 'device:read', keywords: ['blf', 'keys'] },
    ],
  },
  {
    // The client's own contacts (docs/32). Nobody above the account holds phonebook:read, so the
    // console never shows it to staff.
    key: 'phonebook', label: 'Phonebook', icon: 'contacts',
    items: [
      { key: 'contacts', label: 'Contacts', permission: 'phonebook:read', keywords: ['address book', 'people'] },
      { key: 'companies', label: 'Companies', permission: 'phonebook:read', keywords: ['customers', 'organisations'] },
    ],
  },
  {
    key: 'numbers', label: 'Numbers & lines', icon: 'hash',
    items: [
      { key: 'numbers', label: 'Assigned numbers', permission: 'number:read', keywords: ['did', 'phone number', 'where calls go'] },
      // Caller IDs that take turns, for campaigns (0137). Only campaigns use a pool.
      { key: 'number-pools', label: 'Number pools', permission: 'number:read', module: 'dialler', keywords: ['caller id rotation'] },
      // Kept while not built: clients will submit porting requests here (owner, 26 September 2026).
      { key: 'porting', label: 'Porting', phase: 'Phase 2', permission: 'port:read', keywords: ['move a number', 'transfer number'] },
      // connectivity:read, not trunk:read: customers see this too -- which carrier carries their
      // calls and whether it is up. Staff get more.
      { key: 'trunks', label: 'Trunks', permission: 'connectivity:read', keywords: ['carrier', 'sip trunk', 'byot', 'own trunk'] },
      // Microsoft Teams calling through ArcNet, Direct Routing (docs/44).
      { key: 'teams', label: 'Microsoft Teams', permission: 'teams:read', feature: 'teams', keywords: ['direct routing', 'microsoft 365'] },
    ],
  },
  {
    // Where a call goes, in and out, and when.
    key: 'routing', label: 'Call routing', icon: 'ivr',
    items: [
      { key: 'inbound', label: 'Inbound routes', permission: 'number:read', keywords: ['call flow', 'flows', 'incoming', 'after hours'] },
      { key: 'ivrs', label: 'IVR menus', permission: 'flow:read', keywords: ['auto attendant', 'press 1', 'menu'] },
      // Messages and hours checks: steps of a flow, editable here in Classic too (docs/41 §3).
      { key: 'call-steps', label: 'Call steps', permission: 'flow:read', keywords: ['message', 'hours check'] },
      { key: 'ring-groups', label: 'Hunt lists', permission: 'extension:read', keywords: ['ring group', 'ring all'] },
      { key: 'queues', label: 'Queues', permission: 'queue:read', keywords: ['call centre', 'hold'] },
      { key: 'schedules', label: 'Hours & schedules', permission: 'extension:read', keywords: ['business hours', 'opening hours', 'after hours', 'time'] },
      { key: 'holidays', label: 'Public holidays', permission: 'extension:read', keywords: ['closed'] },
      { key: 'outbound', label: 'Outbound routing', permission: 'outboundroute:read', keywords: ['carrier', 'failover', 'prefix'] },
      { key: 'dial-permissions', label: 'Dialling restrictions', permission: 'extension:read', keywords: ['international', 'block dialling', 'policy'] },
      // Account-wide only. A person's own forwarding, screening and hot desking are on their extension.
      { key: 'screening', label: 'Blocked numbers', permission: 'extension:read', keywords: ['block caller', 'spam', 'screening', 'blacklist'] },
    ],
  },
  {
    key: 'features', label: 'Voice features', icon: 'phone',
    items: [
      { key: 'voicemail', label: 'Voicemail', permission: 'voicemail:read', keywords: ['voicemail to email', 'mailbox', 'messages'] },
      { key: 'recording', label: 'Call recording', permission: 'recording:read', keywords: ['recordings', 'retention', 'popia'] },
      { key: 'prompts', label: 'Prompts & greetings', permission: 'extension:read', keywords: ['audio', 'announcement', 'text to speech', 'upload'] },
      { key: 'moh', label: 'Music on hold', permission: 'extension:read', keywords: ['hold music', 'moh'] },
      { key: 'conference', label: 'Conference rooms', permission: 'queue:read', keywords: ['bridge', 'meeting'] },
      { key: 'parking', label: 'Parking lots', permission: 'queue:read', keywords: ['park'] },
      { key: 'feature-codes', label: 'Feature codes', permission: 'tenant:read', keywords: ['star codes', 'short codes', '*'] },
    ],
  },
  {
    // The people who answer the queues, and the words they work from.
    key: 'contact-centre', label: 'Contact centre', icon: 'queue',
    items: [
      { key: 'agents', label: 'Agents & skills', permission: 'queue:read', keywords: ['tier', 'skill'] },
      // Why somebody paused, and what a call was.
      { key: 'agent-codes', label: 'Agent codes', permission: 'queue:read', keywords: ['disposition', 'wrap up', 'pause reason'] },
      // WhatsApp and Telegram (docs/43).
      { key: 'channels', label: 'WhatsApp & Telegram', permission: 'channel:read', feature: 'conversations', keywords: ['chat', 'messaging'] },
      { key: 'conversations', label: 'Conversations', permission: 'conversation:read', feature: 'conversations', keywords: ['chats', 'whatsapp'] },
    ],
  },
  {
    // The people a campaign will call (docs/36 D1b). The client's own: nobody above the account
    // holds dialler:read, so the console never shows it to staff.
    key: 'dialler', label: 'Campaigns', icon: 'callOut',
    items: [
      { key: 'campaigns', label: 'Campaigns', permission: 'campaign:read', module: 'dialler', keywords: ['dialler', 'outbound campaign', 'predictive'] },
      { key: 'lead-lists', label: 'Lead lists', permission: 'dialler:read', module: 'dialler', keywords: ['leads', 'import', 'csv'] },
      { key: 'call-scripts', label: 'Call scripts', permission: 'campaign:read', module: 'dialler', keywords: ['script'] },
      { key: 'do-not-call', label: 'Do not call', permission: 'dialler:read', module: 'dialler', keywords: ['dnc', 'opt out'] },
    ],
  },
  {
    // Every report (owner, 26 September 2026: "all the reporting analytics under reporting").
    key: 'reporting', label: 'Reports', icon: 'rates',
    items: [
      { key: 'calls', label: 'Call history', permission: 'cdr:read', keywords: ['cdr', 'recordings', 'calls', 'missed calls', 'export'] },
      { key: 'queue-reports', label: 'Queue performance', permission: 'queue:read', keywords: ['service level', 'abandoned'] },
      // Who was logged on and what their time went on, today or any past day.
      { key: 'agent-activity', label: 'Agent activity', permission: 'cdr:read', keywords: ['attendance', 'login time', 'breaks'] },
      // How much of the month each phone could take a call (1 October 2026).
      { key: 'phone-availability', label: 'Phone availability', permission: 'extension:read', keywords: ['uptime', 'offline'] },
      // Every campaign side by side, and each one's analytics.
      { key: 'campaign-reports', label: 'Campaign results', permission: 'campaign:read', module: 'dialler', keywords: ['campaign analytics'] },
      // Calls transcribed and analysed on the account's own keys (docs/37).
      { key: 'auto-qa', label: 'Auto QA', permission: 'qa:read', module: 'auto_qa', keywords: ['quality', 'transcript', 'scorecard'] },
      // Reports that build and email themselves (0146, 1 October 2026).
      { key: 'scheduled-reports', label: 'Scheduled reports', permission: 'report:schedule', keywords: ['email report'] },
    ],
  },
  {
    key: 'billing', label: 'Billing', icon: 'billing',
    items: [
      { key: 'billing', label: 'Balance & payments', permission: 'billing:read', keywords: ['invoice', 'statement', 'receipt', 'payment', 'top up', 'airtime', 'ledger'] },
      { key: 'usage', label: 'Usage', permission: 'billing:read', keywords: ['usage breakdown', 'cost', 'charges'] },
      // Spend against the month's cap and each extension's budget (0132).
      { key: 'spend', label: 'Spend limits', permission: 'cdr:read', keywords: ['budget', 'cap'] },
    ],
  },
  {
    key: 'settings', label: 'Settings', icon: 'settings',
    items: [
      { key: 'profile', label: 'Company profile', permission: 'tenant:read', keywords: ['address', 'company name', 'timezone', 'sip domain'] },
      // The account's identity documents, required before numbers, airtime and outside calls (0170).
      { key: 'rica', label: 'RICA verification', permission: 'rica:read', keywords: ['rica', 'kyc', 'id document', 'cipc', 'proof of address', 'verification'] },
      // How Auto QA is set up: its rules, scorecards, prompts and providers (3 October 2026).
      { key: 'auto-qa-setup', label: 'Auto QA setup', permission: 'qa:read', module: 'auto_qa', keywords: ['deepgram', 'openai', 'anthropic'] },
      { key: 'api-keys', label: 'API keys', permission: 'apikey:read', keywords: ['integration', 'token'] },
      // A CRM ringing a seat and dialling for it (0174).
      { key: 'click-to-dial', label: 'Click-to-dial', permission: 'apikey:read', keywords: ['crm', 'hubspot', 'salesforce', 'originate', 'integration'] },
      { key: 'audit', label: 'Audit log', permission: 'audit:read', keywords: ['history', 'who changed'] },
    ],
  },
];

/** Which group a section belongs to, so the right group opens on load. */
export function groupFor(itemKey: string): string | null {
  for (const g of CONFIG_NAV) {
    if (g.items.some((i) => i.key === itemKey)) return g.key;
  }
  return null;
}

/**
 * The menu entries a search matches, by label first and then by keyword, with the word that
 * matched when it is not in the label ("invoice" → Balance & payments, "matched invoice").
 */
export function findPages(groups: ReadonlyArray<ConfigGroup>, query: string): Array<{ group: ConfigGroup; item: ConfigItem; via: string | null }> {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: Array<{ group: ConfigGroup; item: ConfigItem; via: string | null; rank: number }> = [];
  for (const group of groups) {
    for (const item of group.items) {
      const label = item.label.toLowerCase();
      if (label.includes(q)) { out.push({ group, item, via: null, rank: label.startsWith(q) ? 0 : 1 }); continue; }
      const kw = item.keywords?.find((k) => k.toLowerCase().includes(q));
      if (kw) out.push({ group, item, via: kw, rank: 2 });
      else if (group.label.toLowerCase().includes(q)) out.push({ group, item, via: null, rank: 3 });
    }
  }
  return out.sort((a, b) => a.rank - b.rank).map(({ group, item, via }) => ({ group, item, via }));
}
